import React, { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import NavDrawer from './NavDrawer';

// Brand color tokens
const C = {
  gold: '#f0c040',
  goldDim: '#c9a227',
  cyan: '#00d4ff',
  green: '#00e87a',
  purple: '#b060ff',
  red: '#ff4444',
  ink: '#e8ecf5',
  inkSoft: '#7a9ab8',
  inkFaint: '#4a5a7a',
  uiBg: '#04060f',
  cardBg: '#0a1020',
  border: '#1a2540',
  rowBg: 'rgba(8,12,26,0.55)',
};

const STAT_COLORS = ['gold', 'cyan', 'green', 'purple', 'red'];
const PULL_TIERS = ['normal', 'rare', 'mythic'];

const RESPONSIVE_CSS = `
.ed-page {
  min-height: 100vh;
  background: ${C.uiBg};
  font-family: 'Rajdhani', sans-serif;
  color: ${C.ink};
  -webkit-font-smoothing: antialiased;
}
.ed-wrap {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 20px 80px;
}
.ed-input, .ed-textarea, .ed-select {
  width: 100%;
  background: ${C.cardBg};
  border: 1px solid ${C.border};
  color: #c8d8f0;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  padding: 7px 10px;
  border-radius: 2px;
  outline: none;
  box-sizing: border-box;
}
.ed-input:focus, .ed-textarea:focus, .ed-select:focus {
  border-color: ${C.cyan};
}
.ed-textarea {
  resize: vertical;
  min-height: 70px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 13px;
}
.ed-label {
  font-family: 'Orbitron', monospace;
  font-size: 9px;
  letter-spacing: 1.5px;
  color: ${C.inkFaint};
  margin-bottom: 4px;
  display: block;
}
.ed-section {
  background: ${C.rowBg};
  border: 1px solid ${C.border};
  border-radius: 4px;
  margin-bottom: 12px;
}
.ed-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
}
.ed-section-head.open { border-bottom: 1px solid ${C.border}; }
.ed-section-title {
  font-family: 'Orbitron', monospace;
  font-size: 11px;
  letter-spacing: 2.5px;
  color: ${C.gold};
  font-weight: 700;
}
.ed-section-count {
  color: ${C.inkFaint};
  font-weight: 400;
  margin-left: 8px;
}
.ed-section-body { padding: 16px; }
.ed-row3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}
.ed-row2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}
.ed-stat-row {
  display: grid;
  grid-template-columns: 80px 1fr 1fr 110px 28px;
  gap: 8px;
  align-items: end;
  padding: 8px 0;
  border-bottom: 1px dotted ${C.border};
}
.ed-hb-row {
  display: grid;
  grid-template-columns: 80px 1fr 28px;
  gap: 8px;
  align-items: end;
  padding: 8px 0;
  border-bottom: 1px dotted ${C.border};
}
.ed-pr-row {
  display: grid;
  grid-template-columns: 1fr 110px 110px 28px;
  gap: 8px;
  align-items: end;
  padding: 8px 0;
  border-bottom: 1px dotted ${C.border};
}
.ed-x {
  background: transparent;
  border: 0;
  color: ${C.red};
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  height: 32px;
}
.ed-add {
  background: transparent;
  color: ${C.cyan};
  border: 1px dashed ${C.cyan};
  font-family: 'Orbitron', monospace;
  font-size: 10px;
  letter-spacing: 1.5px;
  padding: 7px 14px;
  text-decoration: none;
  border-radius: 2px;
  margin-top: 10px;
  cursor: pointer;
}
.ed-btn {
  font-family: 'Orbitron', monospace;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 1.5px;
  padding: 9px 14px;
  text-decoration: none;
  border-radius: 2px;
  cursor: pointer;
  border: 1px solid ${C.border};
  background: transparent;
  color: ${C.inkSoft};
  display: inline-block;
}
.ed-btn.primary { background: rgba(0,212,255,0.1); color: ${C.cyan}; border-color: ${C.cyan}; }
.ed-btn.danger  { color: ${C.red}; border-color: rgba(255,68,68,0.3); }
@media (max-width: 720px) {
  .ed-row3, .ed-row2 { grid-template-columns: 1fr; }
  .ed-stat-row { grid-template-columns: 1fr 1fr 28px; row-gap: 4px; }
  .ed-stat-row > :nth-child(1) { grid-column: 1 / -1; }
  .ed-pr-row { grid-template-columns: 1fr 1fr 28px; }
}
`;

// Parse a 2-color simple linear gradient. Returns null if it's not the simple pattern.
function parseSimpleGradient(css) {
  if (!css) return null;
  const s = String(css).trim();
  if (!/^linear-gradient\s*\(/i.test(s)) return null;
  const hexes = s.match(/#[0-9a-fA-F]{3,8}/g);
  if (!hexes || hexes.length < 2) return null;
  if (hexes.length > 2) return null;
  return { from: hexes[0], to: hexes[hexes.length - 1] };
}

function buildSimpleGradient(from, to) {
  return `linear-gradient(135deg, ${from || '#ffffff'} 0%, ${to || '#cccccc'} 100%)`;
}

function normalizeHex(h) {
  if (!h) return '#ffffff';
  let s = String(h).trim();
  if (!s.startsWith('#')) s = '#' + s;
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    s = '#' + s[1]+s[1] + s[2]+s[2] + s[3]+s[3];
  }
  if (/^#[0-9a-fA-F]{8}$/.test(s)) {
    s = s.slice(0, 7);
  }
  if (/^#[0-9a-fA-F]{4}$/.test(s)) {
    s = '#' + s[1]+s[1] + s[2]+s[2] + s[3]+s[3];
  }
  return /^#[0-9a-fA-F]{6}$/.test(s) ? s.toLowerCase() : '#ffffff';
}

function navigateTo(href) {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// --- Auto-populate from /src/sets/*.js at build time ---
// require.context is webpack/CRA-specific and loads all matching files into
// a registry, so SET_FILES['2025-smugglers-outpost'] gives us that file's exports.
const _setFilesContext = require.context('./sets', false, /\.js$/);
const SET_FILES = {};
_setFilesContext.keys().forEach(key => {
  const id = key.replace(/^\.\//, '').replace(/\.js$/, '');
  try { SET_FILES[id] = _setFilesContext(key); } catch (_) {}
});

function autoPopulateSubsets(setId) {
  const file = SET_FILES[setId];
  if (!file || !Array.isArray(file.SETS)) {
    return { base: [], insert: [], auto: [], sketch: [] };
  }
  const result = { base: [], insert: [], auto: [], sketch: [] };
  for (const sub of file.SETS) {
    const type = (sub.type || 'base').toLowerCase();
    const bucket = ['base', 'insert', 'auto', 'sketch'].includes(type) ? type : 'base';
    const cards = (sub.cards || []).map(c => {
      const card = { num: c.n || '', title: c.name || '' };
      const m = c.artist || c.meta;
      if (m) card.meta = m;
      return card;
    });
    result[bucket].push({
      count: cards.length,
      name: (sub.title || sub.name || '').toUpperCase(),
      meta: '',
      odds: '',
      parallels: 0,
      cards,
    });
  }
  return result;
}

function emptySummary(setId, registryEntry) {
  const yearMatch = setId.match(/^(\d{4})/);
  return {
    set_id: setId,
    status: 'draft',
    year: yearMatch ? yearMatch[1] : '',
    brand: registryEntry?.brand?.toUpperCase() || '',
    title: registryEntry?.shortName?.toUpperCase().replace(/\s+\d{4}$/, '') || '',
    release_date: registryEntry?.releaseDate || '',
    lede: '',
    stats: [],
    hobby_box: [],
    pull_rates: [],
    parallel_rainbow: [],
    artists: [],
    subsets: autoPopulateSubsets(setId),
  };
}

export default function AdminSetSummaryEdit({ user, setId }) {
  const registryEntry = SET_REGISTRY.find(s => s.id === setId);

  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [openSections, setOpenSections] = useState({ hero: true, stats: false, hobby: false, pulls: false, parallel: false, artists: false, subsets: false });
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSaved, setLastSaved] = useState(null);
  const [subsetsTab, setSubsetsTab] = useState('base');
  const [expandedSubsets, setExpandedSubsets] = useState({});

  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data: profile, error: pErr } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (cancelled) return;

      if (pErr || !profile?.is_admin) {
        navigateTo('/');
        return;
      }
      setIsAdmin(true);

      const { data: row } = await supabase
        .from('set_summaries')
        .select('*')
        .eq('set_id', setId)
        .maybeSingle();

      if (cancelled) return;

      if (row) {
        setData({
          ...row,
          stats: row.stats || [],
          hobby_box: row.hobby_box || [],
          pull_rates: row.pull_rates || [],
          parallel_rainbow: row.parallel_rainbow || [],
          artists: row.artists || [],
          subsets: row.subsets || { base: [], insert: [], auto: [], sketch: [] },
        });
        setLastSaved(row.updated_at);
      } else {
        setData(emptySummary(setId, registryEntry));
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [user.id, setId, registryEntry]);

  const performSave = useCallback(async (snapshot) => {
    if (!snapshot) return;
    setSaveStatus('saving');
    const payload = {
      set_id: snapshot.set_id,
      status: snapshot.status,
      year: snapshot.year || '',
      brand: snapshot.brand || '',
      title: snapshot.title || '',
      release_date: snapshot.release_date || null,
      lede: snapshot.lede || null,
      stats: snapshot.stats || [],
      hobby_box: snapshot.hobby_box || [],
      pull_rates: snapshot.pull_rates || [],
      parallel_rainbow: snapshot.parallel_rainbow || [],
      artists: snapshot.artists || [],
      subsets: snapshot.subsets || { base: [], insert: [], auto: [], sketch: [] },
      updated_by: user.id,
    };
    const { error } = await supabase
      .from('set_summaries')
      .upsert(payload, { onConflict: 'set_id' });
    if (error) {
      setSaveStatus('error');
      console.error('Auto-save failed:', error);
    } else {
      setSaveStatus('saved');
      setLastSaved(new Date().toISOString());
    }
  }, [user.id]);

  useEffect(() => {
    if (!data) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      performSave(data);
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [data, performSave]);

  const updateField = (key, value) => setData(prev => ({ ...prev, [key]: value }));
  const updateArrayItem = (key, idx, patch) => setData(prev => ({
    ...prev,
    [key]: prev[key].map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));
  const addArrayItem = (key, item) => setData(prev => ({
    ...prev,
    [key]: [...prev[key], item],
  }));
  const removeArrayItem = (key, idx) => setData(prev => ({
    ...prev,
    [key]: prev[key].filter((_, i) => i !== idx),
  }));

  const toggleSection = (k) => setOpenSections(prev => ({ ...prev, [k]: !prev[k] }));

  const toggleSubset = (key) => setExpandedSubsets(prev => ({ ...prev, [key]: !prev[key] }));

  const updateSubset = (tab, idx, patch) => setData(prev => ({
    ...prev,
    subsets: {
      ...prev.subsets,
      [tab]: prev.subsets[tab].map((it, i) => i === idx ? { ...it, ...patch } : it),
    },
  }));

  const addSubset = (tab) => setData(prev => ({
    ...prev,
    subsets: {
      ...prev.subsets,
      [tab]: [...(prev.subsets[tab] || []), { count: 0, name: '', meta: '', odds: '', parallels: 0, cards: [] }],
    },
  }));

  const removeSubset = (tab, idx) => {
    const sub = data.subsets[tab][idx];
    const cardCount = (sub?.cards || []).length;
    const msg = cardCount > 0
      ? `Delete sub-set "${sub.name || '(unnamed)'}" and its ${cardCount} card${cardCount === 1 ? '' : 's'}? This cannot be undone.`
      : `Delete sub-set "${sub?.name || '(unnamed)'}"?`;
    if (!window.confirm(msg)) return;
    setData(prev => ({
      ...prev,
      subsets: {
        ...prev.subsets,
        [tab]: prev.subsets[tab].filter((_, i) => i !== idx),
      },
    }));
  };

  // Convert cards array <-> tab-separated text. One card per line.
  const cardsToText = (cards) => (cards || [])
    .map(c => [c.num || '', c.title || '', c.meta || ''].filter(x => x !== '').join('\t'))
    .join('\n');

  const textToCards = (text) => text.split('\n')
    .map(l => l.replace(/\r$/, ''))
    .filter(l => l.trim() !== '')
    .map(l => {
      const parts = l.split('\t');
      const card = { num: (parts[0] || '').trim(), title: (parts[1] || '').trim() };
      if (parts[2] && parts[2].trim() !== '') card.meta = parts[2].trim();
      return card;
    });

  const handlePublish = async () => {
    setData(prev => ({ ...prev, status: 'published' }));
  };
  const handleUnpublish = async () => {
    if (!window.confirm('Move this back to draft? It will no longer be publicly visible.')) return;
    setData(prev => ({ ...prev, status: 'draft' }));
  };

  if (loading || !data) {
    return (
      <div className="ed-page">
        <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
        {user && <NavDrawer user={user} />}
        <div className="ed-wrap" style={{ textAlign: 'center', paddingTop: '120px' }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', color: C.cyan, letterSpacing: '4px' }}>
            // LOADING EDITOR
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const isPublished = data.status === 'published';
  const displayName = registryEntry?.shortName || setId;

  let saveLabel;
  if (saveStatus === 'saving') saveLabel = 'saving…';
  else if (saveStatus === 'error') saveLabel = 'auto-save failed';
  else if (lastSaved) {
    const secs = Math.floor((Date.now() - new Date(lastSaved).getTime()) / 1000);
    if (secs < 5) saveLabel = 'auto-saved just now';
    else if (secs < 60) saveLabel = `auto-saved ${secs} seconds ago`;
    else saveLabel = `auto-saved ${Math.floor(secs/60)} min ago`;
  } else saveLabel = 'unsaved';

  return (
    <div className="ed-page">
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      <NavDrawer user={user} />
      <div className="ed-wrap">

        <div style={{ marginBottom: '14px' }}>
          <a
            href="/admin/set-summaries"
            onClick={(e) => { e.preventDefault(); navigateTo('/admin/set-summaries'); }}
            style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.cyan, letterSpacing: '2px', textDecoration: 'none' }}
          >
            ← BACK TO ALL SETS
          </a>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '24px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '4px', color: C.goldDim, marginBottom: '6px' }}>
              EDITING SET SUMMARY
            </div>
            <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '22px', fontWeight: 800, color: '#fff', letterSpacing: '1px' }}>
              {displayName}
            </div>
            <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: C.inkFaint, marginTop: '4px' }}>
              setId: {setId} · {saveLabel}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
            <a
              href={`/resources/sets/${setId}?preview=draft`}
              target="_blank"
              rel="noreferrer"
              className="ed-btn"
            >
              PREVIEW ↗
            </a>
            {isPublished ? (
              <button type="button" className="ed-btn danger" onClick={handleUnpublish}>UNPUBLISH</button>
            ) : (
              <button type="button" className="ed-btn primary" onClick={handlePublish}>PUBLISH</button>
            )}
          </div>
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.hero ? 'open' : ''}`} onClick={() => toggleSection('hero')}>
            <div className="ed-section-title">// HERO</div>
            <span style={{ color: C.inkFaint }}>{openSections.hero ? '▾' : '▸'}</span>
          </div>
          {openSections.hero && (
            <div className="ed-section-body">
              <div className="ed-row3">
                <div>
                  <label className="ed-label">YEAR</label>
                  <input className="ed-input" value={data.year} onChange={e => updateField('year', e.target.value)} />
                </div>
                <div>
                  <label className="ed-label">BRAND</label>
                  <input className="ed-input" value={data.brand} onChange={e => updateField('brand', e.target.value)} />
                </div>
                <div>
                  <label className="ed-label">RELEASE DATE</label>
                  <input className="ed-input" value={data.release_date || ''} onChange={e => updateField('release_date', e.target.value)} placeholder="e.g. 12 / 10 / 2025" />
                </div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label className="ed-label">TITLE</label>
                <input className="ed-input" value={data.title} onChange={e => updateField('title', e.target.value)} />
              </div>
              <div>
                <label className="ed-label">LEDE</label>
                <textarea className="ed-textarea" rows={3} value={data.lede || ''} onChange={e => updateField('lede', e.target.value)} placeholder="A 1-2 sentence description shown at the top of the public page." />
              </div>
            </div>
          )}
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.stats ? 'open' : ''}`} onClick={() => toggleSection('stats')}>
            <div className="ed-section-title">
              // STATS GRID
              <span className="ed-section-count">{data.stats.length} item{data.stats.length === 1 ? '' : 's'}</span>
            </div>
            <span style={{ color: C.inkFaint }}>{openSections.stats ? '▾' : '▸'}</span>
          </div>
          {openSections.stats && (
            <div className="ed-section-body">
              <div style={{ fontSize: '11px', color: C.inkFaint, marginBottom: '8px' }}>
                The 5 colored stat boxes near the top of the page (Base Cards, Insert Sets, etc).
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 110px 28px', gap: '8px', padding: '4px 0', fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1.5px', color: C.inkFaint }}>
                <div>NUM</div><div>LABEL</div><div>SUB</div><div>COLOR</div><div></div>
              </div>
              {data.stats.map((s, i) => (
                <div key={i} className="ed-stat-row">
                  <input className="ed-input" value={s.num || ''} onChange={e => updateArrayItem('stats', i, { num: e.target.value })} placeholder="100" />
                  <input className="ed-input" value={s.label || ''} onChange={e => updateArrayItem('stats', i, { label: e.target.value })} placeholder="Base Cards" />
                  <input className="ed-input" value={s.sub || ''} onChange={e => updateArrayItem('stats', i, { sub: e.target.value })} placeholder="Mixed art / archives" />
                  <select className="ed-select" value={s.color || 'gold'} onChange={e => updateArrayItem('stats', i, { color: e.target.value })}>
                    {STAT_COLORS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                  </select>
                  <button type="button" className="ed-x" onClick={() => removeArrayItem('stats', i)}>×</button>
                </div>
              ))}
              <button type="button" className="ed-add" onClick={() => addArrayItem('stats', { num: '', label: '', sub: '', color: 'gold' })}>+ ADD STAT</button>
            </div>
          )}
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.hobby ? 'open' : ''}`} onClick={() => toggleSection('hobby')}>
            <div className="ed-section-title">
              // HOBBY BOX CONFIG
              <span className="ed-section-count">{data.hobby_box.length} item{data.hobby_box.length === 1 ? '' : 's'}</span>
            </div>
            <span style={{ color: C.inkFaint }}>{openSections.hobby ? '▾' : '▸'}</span>
          </div>
          {openSections.hobby && (
            <div className="ed-section-body">
              <div style={{ fontSize: '11px', color: C.inkFaint, marginBottom: '8px' }}>
                Box configuration facts (e.g. "18 packs per box").
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 28px', gap: '8px', padding: '4px 0', fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1.5px', color: C.inkFaint }}>
                <div>VALUE</div><div>LABEL</div><div></div>
              </div>
              {data.hobby_box.map((b, i) => (
                <div key={i} className="ed-hb-row">
                  <input className="ed-input" value={b.v || ''} onChange={e => updateArrayItem('hobby_box', i, { v: e.target.value })} placeholder="18" />
                  <input className="ed-input" value={b.l || ''} onChange={e => updateArrayItem('hobby_box', i, { l: e.target.value })} placeholder="Packs per box" />
                  <button type="button" className="ed-x" onClick={() => removeArrayItem('hobby_box', i)}>×</button>
                </div>
              ))}
              <button type="button" className="ed-add" onClick={() => addArrayItem('hobby_box', { v: '', l: '' })}>+ ADD ROW</button>
            </div>
          )}
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.pulls ? 'open' : ''}`} onClick={() => toggleSection('pulls')}>
            <div className="ed-section-title">
              // PULL RATES
              <span className="ed-section-count">{data.pull_rates.length} item{data.pull_rates.length === 1 ? '' : 's'}</span>
            </div>
            <span style={{ color: C.inkFaint }}>{openSections.pulls ? '▾' : '▸'}</span>
          </div>
          {openSections.pulls && (
            <div className="ed-section-body">
              <div style={{ fontSize: '11px', color: C.inkFaint, marginBottom: '8px' }}>
                Insert pull rates (e.g. "Galactic Inspirations · 1:18 packs · normal").
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 28px', gap: '8px', padding: '4px 0', fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1.5px', color: C.inkFaint }}>
                <div>INSERT NAME</div><div>ODDS</div><div>TIER</div><div></div>
              </div>
              {data.pull_rates.map((r, i) => (
                <div key={i} className="ed-pr-row">
                  <input className="ed-input" value={r.name || ''} onChange={e => updateArrayItem('pull_rates', i, { name: e.target.value })} placeholder="Galactic Inspirations" />
                  <input className="ed-input" value={r.odds || ''} onChange={e => updateArrayItem('pull_rates', i, { odds: e.target.value })} placeholder="1 : 18" />
                  <select className="ed-select" value={r.tier || 'normal'} onChange={e => updateArrayItem('pull_rates', i, { tier: e.target.value })}>
                    {PULL_TIERS.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                  </select>
                  <button type="button" className="ed-x" onClick={() => removeArrayItem('pull_rates', i)}>×</button>
                </div>
              ))}
              <button type="button" className="ed-add" onClick={() => addArrayItem('pull_rates', { name: '', odds: '', tier: 'normal' })}>+ ADD PULL RATE</button>
            </div>
          )}
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.parallel ? 'open' : ''}`} onClick={() => toggleSection('parallel')}>
            <div className="ed-section-title">
              // PARALLEL RAINBOW
              <span className="ed-section-count">{data.parallel_rainbow.length} chip{data.parallel_rainbow.length === 1 ? '' : 's'}</span>
            </div>
            <span style={{ color: C.inkFaint }}>{openSections.parallel ? '▾' : '▸'}</span>
          </div>
          {openSections.parallel && (
            <div className="ed-section-body">
              <div style={{ fontSize: '11px', color: C.inkFaint, marginBottom: '8px' }}>
                Each parallel chip on the public page. The gradient CSS preview swatch updates live as you type.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr 36px 28px 28px', gap: '8px', padding: '4px 0', fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1.5px', color: C.inkFaint }}>
                <div>NAME</div><div>PRINT RUN</div><div>COLORS</div><div>SWATCH</div><div>DRK</div><div></div>
              </div>
              {data.parallel_rainbow.map((p, i) => {
                const parsed = parseSimpleGradient(p.gradient);
                const rawMode = p.rawMode || (!parsed && !!p.gradient);
                const fromColor = normalizeHex(parsed?.from || '#ffffff');
                const toColor = normalizeHex(parsed?.to || '#cccccc');
                return (
                  <div key={i} style={{ padding: '10px 0', borderBottom: `1px dotted ${C.border}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr 36px 28px 28px', gap: '8px', alignItems: 'center' }}>
                      <input className="ed-input" value={p.name || ''} onChange={e => updateArrayItem('parallel_rainbow', i, { name: e.target.value })} placeholder="REFRACTOR" />
                      <input className="ed-input" value={p.pr || ''} onChange={e => updateArrayItem('parallel_rainbow', i, { pr: e.target.value })} placeholder="/199" style={{ textAlign: 'center' }} />
                      {rawMode ? (
                        <input className="ed-input" value={p.gradient || ''} onChange={e => updateArrayItem('parallel_rainbow', i, { gradient: e.target.value })} placeholder="linear-gradient(135deg, #fff 0%, #ccc 100%)" style={{ fontSize: '10px' }} />
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: '2px', padding: '4px 6px', cursor: 'pointer' }}>
                            <input type="color" value={fromColor} onChange={e => updateArrayItem('parallel_rainbow', i, { gradient: buildSimpleGradient(e.target.value, toColor) })} style={{ width: '24px', height: '24px', padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }} />
                            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.ink }}>{fromColor}</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: '2px', padding: '4px 6px', cursor: 'pointer' }}>
                            <input type="color" value={toColor} onChange={e => updateArrayItem('parallel_rainbow', i, { gradient: buildSimpleGradient(fromColor, e.target.value) })} style={{ width: '24px', height: '24px', padding: 0, border: 0, background: 'transparent', cursor: 'pointer' }} />
                            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.ink }}>{toColor}</span>
                          </label>
                        </div>
                      )}
                      <div style={{ height: '32px', borderRadius: '2px', background: p.gradient || '#0a1020', border: `1px solid ${C.border}` }} title="Live preview" />
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px', cursor: 'pointer' }} title="Dark text on this chip?">
                        <input type="checkbox" checked={!!p.darkText} onChange={e => updateArrayItem('parallel_rainbow', i, { darkText: e.target.checked })} />
                      </label>
                      <button type="button" className="ed-x" onClick={() => removeArrayItem('parallel_rainbow', i)}>×</button>
                    </div>
                    <div style={{ marginTop: '4px', fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, paddingLeft: '2px' }}>
                      <button type="button" onClick={() => updateArrayItem('parallel_rainbow', i, { rawMode: !rawMode })} style={{ background: 'transparent', border: 0, color: C.cyan, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}>
                        {rawMode ? '↗ use 2-color picker' : '↘ raw CSS mode (radial / 3+ stops)'}
                      </button>
                      {!rawMode && !parsed && p.gradient && <span style={{ color: C.gold, marginLeft: '8px' }}>· could not parse — switching to raw CSS recommended</span>}
                    </div>
                  </div>
                );
              })}
              <button type="button" className="ed-add" onClick={() => addArrayItem('parallel_rainbow', { name: '', pr: '', gradient: 'linear-gradient(135deg, #ffffff 0%, #cccccc 100%)', darkText: false })}>+ ADD PARALLEL</button>
            </div>
          )}
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.artists ? 'open' : ''}`} onClick={() => toggleSection('artists')}>
            <div className="ed-section-title">
              // COMMISSIONED ARTISTS
              <span className="ed-section-count">{data.artists.length} entr{data.artists.length === 1 ? 'y' : 'ies'}</span>
            </div>
            <span style={{ color: C.inkFaint }}>{openSections.artists ? '▾' : '▸'}</span>
          </div>
          {openSections.artists && (
            <div className="ed-section-body">
              <div style={{ fontSize: '11px', color: C.inkFaint, marginBottom: '8px' }}>
                Insert artists credited on the page (e.g. "Joe Corroney · Galactic Inspirations").
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: '8px', padding: '4px 0', fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1.5px', color: C.inkFaint }}>
                <div>NAME</div><div>WORK</div><div></div>
              </div>
              {data.artists.map((a, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: '8px', padding: '8px 0', alignItems: 'center', borderBottom: `1px dotted ${C.border}` }}>
                  <input className="ed-input" value={a.name || ''} onChange={e => updateArrayItem('artists', i, { name: e.target.value })} placeholder="Joe Corroney" />
                  <input className="ed-input" value={a.work || ''} onChange={e => updateArrayItem('artists', i, { work: e.target.value })} placeholder="Galactic Inspirations + Skeleton Crew Puzzle" />
                  <button type="button" className="ed-x" onClick={() => removeArrayItem('artists', i)}>×</button>
                </div>
              ))}
              <button type="button" className="ed-add" onClick={() => addArrayItem('artists', { name: '', work: '' })}>+ ADD ARTIST</button>
            </div>
          )}
        </div>

        <div className="ed-section">
          <div className={`ed-section-head ${openSections.subsets ? 'open' : ''}`} onClick={() => toggleSection('subsets')}>
            <div className="ed-section-title">
              // SUB-SETS
              <span className="ed-section-count">
                {(data.subsets.base?.length || 0) + (data.subsets.insert?.length || 0) + (data.subsets.auto?.length || 0) + (data.subsets.sketch?.length || 0)} sub-set
                {(data.subsets.base?.length || 0) + (data.subsets.insert?.length || 0) + (data.subsets.auto?.length || 0) + (data.subsets.sketch?.length || 0) === 1 ? '' : 's'}
              </span>
            </div>
            <span style={{ color: C.inkFaint }}>{openSections.subsets ? '▾' : '▸'}</span>
          </div>
          {openSections.subsets && (
            <div className="ed-section-body">
              <div style={{ fontSize: '11px', color: C.inkFaint, marginBottom: '12px' }}>
                Card listings grouped by type. Tap a sub-set to expand and edit cards.
              </div>

              {/* Tab buttons */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {['base', 'insert', 'auto', 'sketch'].map(tab => {
                  const count = data.subsets[tab]?.length || 0;
                  const active = subsetsTab === tab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setSubsetsTab(tab)}
                      style={{
                        background: active ? 'rgba(0,212,255,0.1)' : 'transparent',
                        color: active ? C.cyan : C.inkSoft,
                        border: `1px solid ${active ? C.cyan : C.border}`,
                        fontFamily: "'Orbitron', monospace",
                        fontWeight: 700,
                        fontSize: '10px',
                        letterSpacing: '2px',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        borderRadius: '2px',
                      }}
                    >
                      {tab.toUpperCase()} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Sub-sets list for the active tab */}
              {(data.subsets[subsetsTab] || []).map((s, i) => {
                const key = `${subsetsTab}-${i}`;
                const expanded = !!expandedSubsets[key];
                const cardCount = (s.cards || []).length;
                return (
                  <div key={key} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: '3px', marginBottom: '8px' }}>
                    <div
                      onClick={() => toggleSubset(key)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div style={{ minWidth: 0, display: 'flex', gap: '10px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '12px', color: C.ink, fontWeight: 700, letterSpacing: '1px' }}>
                          {s.name || '(unnamed sub-set)'}
                        </span>
                        <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint }}>
                          {s.count || 0} cards declared {cardCount !== (s.count || 0) && <span style={{ color: C.gold }}>· {cardCount} in list</span>}
                        </span>
                      </div>
                      <span style={{ color: C.inkFaint, fontSize: '12px' }}>{expanded ? '▾' : '▸'}</span>
                    </div>
                    {expanded && (
                      <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${C.border}` }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 110px', gap: '10px', marginTop: '12px' }}>
                          <div>
                            <label className="ed-label">COUNT</label>
                            <input className="ed-input" type="number" value={s.count || 0} onChange={e => updateSubset(subsetsTab, i, { count: parseInt(e.target.value, 10) || 0 })} />
                          </div>
                          <div>
                            <label className="ed-label">NAME</label>
                            <input className="ed-input" value={s.name || ''} onChange={e => updateSubset(subsetsTab, i, { name: e.target.value })} placeholder="BASE SET" />
                          </div>
                          <div>
                            <label className="ed-label">PARALLELS #</label>
                            <input className="ed-input" type="number" value={s.parallels || 0} onChange={e => updateSubset(subsetsTab, i, { parallels: parseInt(e.target.value, 10) || 0 })} />
                          </div>
                        </div>
                        <div style={{ marginTop: '10px' }}>
                          <label className="ed-label">META</label>
                          <input className="ed-input" value={s.meta || ''} onChange={e => updateSubset(subsetsTab, i, { meta: e.target.value })} placeholder="Original art + reimagined classics" />
                        </div>
                        <div style={{ marginTop: '10px' }}>
                          <label className="ed-label">ODDS</label>
                          <input className="ed-input" value={s.odds || ''} onChange={e => updateSubset(subsetsTab, i, { odds: e.target.value })} placeholder="1:18 packs" />
                        </div>
                        <div style={{ display: 'flex', gap: '20px', marginTop: '12px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.inkSoft, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!s.oneOfOne} onChange={e => updateSubset(subsetsTab, i, { oneOfOne: e.target.checked })} />
                            ONE-OF-ONE ONLY (1/1)
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.inkSoft, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!s.noParallels} onChange={e => updateSubset(subsetsTab, i, { noParallels: e.target.checked })} />
                            NO PARALLELS
                          </label>
                        </div>
                        <div style={{ marginTop: '14px' }}>
                          <label className="ed-label">CARDS · one per line · columns: NUM &lt;TAB&gt; TITLE &lt;TAB&gt; META (optional)</label>
                          <textarea
                            className="ed-textarea"
                            rows={Math.min(20, Math.max(5, cardCount + 2))}
                            value={cardsToText(s.cards)}
                            onChange={e => updateSubset(subsetsTab, i, { cards: textToCards(e.target.value) })}
                            placeholder={"1\tWhat Remains\tCandice Dailey\n2\tLuke Skywalker\tDave Seely"}
                            style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', whiteSpace: 'pre' }}
                          />
                          <div style={{ marginTop: '4px', fontSize: '10px', color: C.inkFaint }}>
                            {cardCount} card{cardCount === 1 ? '' : 's'} in list
                            {cardCount !== (s.count || 0) && (
                              <span style={{ color: C.gold }}> · declared count is {s.count || 0} — mismatch</span>
                            )}
                          </div>
                        </div>
                        <div style={{ marginTop: '14px', textAlign: 'right' }}>
                          <button type="button" className="ed-btn danger" onClick={() => removeSubset(subsetsTab, i)}>DELETE SUB-SET</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <button type="button" className="ed-add" onClick={() => addSubset(subsetsTab)}>+ ADD {subsetsTab.toUpperCase()} SUB-SET</button>
            </div>
          )}
        </div>

        <div style={{ marginTop: '24px', padding: '16px', background: C.rowBg, border: `1px solid ${C.border}`, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: C.inkFaint }}>
            Status: <span style={{ color: isPublished ? C.green : C.gold }}>{data.status.toUpperCase()}</span>
            {isPublished && <span> · Live at /resources/sets/{setId}</span>}
          </div>
        </div>

      </div>
    </div>
  );
}
